import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
  Folder,
  LayoutTemplate,
  X,
} from 'lucide-react';
import { REPLY_TEMPLATE_TYPES, isReplyTemplateType, sortReplyTemplates } from '../utils/replyTemplates';

const STANDARD_TEMPLATE_TYPES = new Set([
  'initial',
  'initial_design',
  'follow_up_1',
  'follow_up_1_design',
  'follow_up_2',
  'follow_up_2_design',
  'follow_up_3',
  'follow_up_3_design',
  ...REPLY_TEMPLATE_TYPES,
]);

function isInitialTemplateType(type) {
  const t = String(type || '').trim();
  return t === 'initial' || t === 'initial_design';
}

function isFollowUp1TemplateType(type) {
  const t = String(type || '').trim();
  return t === 'follow_up_1' || t === 'follow_up_1_design';
}

function isFollowUp2TemplateType(type) {
  const t = String(type || '').trim();
  return t === 'follow_up_2' || t === 'follow_up_2_design';
}

function isFollowUp3TemplateType(type) {
  const t = String(type || '').trim();
  return t === 'follow_up_3' || t === 'follow_up_3_design';
}

const FOLDER_DEFS = [
  { id: 'initial', label: 'Erstkontakt', description: 'Text & Design' },
  { id: 'follow_up_1', label: 'Follow-up 1', description: 'Text & Design' },
  { id: 'follow_up_2', label: 'Follow-up 2', description: 'Text & Design' },
  { id: 'follow_up_3', label: 'Follow-up 3', description: 'Text & Design' },
  { id: 'replies', label: 'Antworten', description: 'Nach Kundenrückmeldung' },
  { id: 'custom', label: 'Eigene Vorlagen', description: 'Selbst angelegte Vorlagen' },
];

function getPrimarySequenceIds(templates = []) {
  const primary = {};
  for (const t of templates) {
    const type = String(t?.type || '').trim();
    if (!STANDARD_TEMPLATE_TYPES.has(type)) continue;
    const id = Number(t.id);
    if (!id) continue;
    if (primary[type] == null || id < primary[type]) primary[type] = id;
  }
  return primary;
}

function isPrimarySequenceTemplate(template, primaryIds) {
  if (!template) return false;
  const type = String(template.type || '').trim();
  if (!STANDARD_TEMPLATE_TYPES.has(type)) return false;
  return Number(primaryIds?.[type]) === Number(template.id);
}

/** Gleiche Ordner-Logik wie auf der Vorlagen-Seite */
export function groupTemplatesIntoFolders(templates = []) {
  const primaryIds = getPrimarySequenceIds(templates);
  const groups = {
    initial: [],
    follow_up_1: [],
    follow_up_2: [],
    follow_up_3: [],
    replies: [],
    custom: [],
  };
  templates.forEach((template) => {
    if (isPrimarySequenceTemplate(template, primaryIds)) {
      if (isReplyTemplateType(template.type)) {
        groups.replies.push(template);
      } else if (isInitialTemplateType(template.type)) {
        groups.initial.push(template);
      } else if (isFollowUp1TemplateType(template.type)) {
        groups.follow_up_1.push(template);
      } else if (isFollowUp2TemplateType(template.type)) {
        groups.follow_up_2.push(template);
      } else if (isFollowUp3TemplateType(template.type)) {
        groups.follow_up_3.push(template);
      } else if (groups[template.type]) {
        groups[template.type].push(template);
      } else {
        groups.custom.push(template);
      }
    } else {
      groups.custom.push(template);
    }
  });
  Object.keys(groups).forEach((key) => {
    if (key === 'replies') {
      groups.replies = sortReplyTemplates(groups.replies);
      return;
    }
    groups[key].sort((a, b) => {
      if (key === 'initial') {
        const order = { initial: 0, initial_design: 1 };
        const oa = order[a.type] ?? 9;
        const ob = order[b.type] ?? 9;
        if (oa !== ob) return oa - ob;
      }
      if (key === 'follow_up_1') {
        const order = { follow_up_1: 0, follow_up_1_design: 1 };
        const oa = order[a.type] ?? 9;
        const ob = order[b.type] ?? 9;
        if (oa !== ob) return oa - ob;
      }
      if (key === 'follow_up_2') {
        const order = { follow_up_2: 0, follow_up_2_design: 1 };
        const oa = order[a.type] ?? 9;
        const ob = order[b.type] ?? 9;
        if (oa !== ob) return oa - ob;
      }
      if (key === 'follow_up_3') {
        const order = { follow_up_3: 0, follow_up_3_design: 1 };
        const oa = order[a.type] ?? 9;
        const ob = order[b.type] ?? 9;
        if (oa !== ob) return oa - ob;
      }
      return String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' });
    });
  });
  return groups;
}

function templateSnippet(template) {
  const subject = String(template?.subject || '').trim();
  if (subject) return subject;
  const raw = String(template?.content || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return raw.slice(0, 80) || 'Ohne Inhalt';
}

/**
 * Vorlagen-Auswahl mit Ordner-Ansicht (wie Medien-Bibliothek).
 */
export function TemplatePickerButton({
  templates = [],
  selectedId = '',
  onSelect,
  label = 'Vorlage wählen…',
  disabled = false,
  className = 'flex-1 min-w-[140px] px-2.5 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-left text-neutral-800 dark:text-neutral-100 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors',
}) {
  const [open, setOpen] = useState(false);
  const [folderId, setFolderId] = useState(null);

  const groups = useMemo(() => groupTemplatesIntoFolders(templates), [templates]);

  const folders = useMemo(
    () =>
      FOLDER_DEFS.map((def) => ({
        ...def,
        count: groups[def.id]?.length || 0,
      })).filter((f) => f.count > 0 || f.id === 'custom'),
    [groups]
  );

  const currentFolder = useMemo(
    () => FOLDER_DEFS.find((f) => f.id === folderId) || null,
    [folderId]
  );

  const folderTemplates = useMemo(() => {
    if (!folderId) return [];
    return groups[folderId] || [];
  }, [groups, folderId]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(selectedId)) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    if (!open) setFolderId(null);
  }, [open]);

  const pick = (template) => {
    onSelect?.(String(template.id));
    setOpen(false);
    setFolderId(null);
  };

  const clear = (e) => {
    e?.stopPropagation?.();
    onSelect?.('');
    setOpen(false);
    setFolderId(null);
  };

  return (
    <>
      <button
        type="button"
        className={`${className}${disabled ? ' opacity-50 cursor-not-allowed' : ''}`}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <LayoutTemplate className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
          <span className="truncate">
            {selectedTemplate ? selectedTemplate.name : label}
          </span>
        </span>
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[240] flex items-center justify-center p-3 sm:p-4">
            <div
              className="absolute inset-0 bg-neutral-900/50 backdrop-blur-[2px]"
              onClick={() => setOpen(false)}
            />
            <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden max-h-[85vh] flex flex-col">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100 dark:border-neutral-800">
                {folderId ? (
                  <button
                    type="button"
                    onClick={() => setFolderId(null)}
                    className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    aria-label="Zurück"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
                    <Folder className="w-4 h-4" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                    {currentFolder ? currentFolder.label : 'Vorlage wählen'}
                  </div>
                  <div className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                    {currentFolder
                      ? currentFolder.description
                      : 'Ordner öffnen und Vorlage auswählen'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                {!folderId ? (
                  <div className="space-y-0.5">
                    {folders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => setFolderId(folder.id)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-neutral-50 dark:hover:bg-neutral-800/80 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-500 dark:text-neutral-400">
                          <Folder className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                            {folder.label}
                          </div>
                          <div className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                            {folder.count} Vorlage{folder.count === 1 ? '' : 'n'}
                            {folder.description ? ` · ${folder.description}` : ''}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                      </button>
                    ))}
                    {selectedId ? (
                      <button
                        type="button"
                        onClick={clear}
                        className="w-full mt-1 px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      >
                        Auswahl aufheben
                      </button>
                    ) : null}
                    <Link
                      to="/templates"
                      className="block mt-1 px-3 py-2 text-[11px] text-primary-600 dark:text-primary-400 hover:underline"
                      onClick={() => setOpen(false)}
                    >
                      Vorlagen verwalten →
                    </Link>
                  </div>
                ) : folderTemplates.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    Keine Vorlagen in diesem Ordner.
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {folderTemplates.map((template) => {
                      const active = String(template.id) === String(selectedId);
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => pick(template)}
                          className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                            active
                              ? 'bg-primary-50 dark:bg-primary-900/20'
                              : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/80'
                          }`}
                        >
                          <div
                            className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              active
                                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400'
                                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500'
                            }`}
                          >
                            <FileText className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                              {template.name}
                            </div>
                            <div className="text-[11px] text-neutral-500 dark:text-neutral-400 line-clamp-2">
                              {templateSnippet(template)}
                            </div>
                          </div>
                          {active ? (
                            <Check className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-1" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default TemplatePickerButton;
